'use client'

import { CommunityEstablishedBadge } from '@/components/_community/community-established-badge'
import Error from '@/components/common/error'
import ArrowLeft from '@/components/icons/arrow-left'
import ArrowRight from '@/components/icons/arrow-right'
import ShieldIcon from '@/components/icons/shield-icon'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useState } from 'react'

export function getCommunityMembersCount(community) {
  if (!community) return 0
  if (community?._stats?.members?.all !== undefined) {
    return community._stats.members.all
  }

  if (Array.isArray(community.members)) {
    return community.members.length
  }

  return 0
}

export default function PartneredCommunities({ featuredCommunities = [] }) {
  const viewAllCommunitiesHref = '/sim-racing-communities'
  const [carouselApi, setCarouselApi] = useState(null)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  useEffect(() => {
    if (!carouselApi) return

    const updateScrollState = () => {
      setCanScrollPrev(carouselApi.canScrollPrev())
      setCanScrollNext(carouselApi.canScrollNext())
    }

    updateScrollState()
    carouselApi.on('select', updateScrollState)
    carouselApi.on('reInit', updateScrollState)

    return () => {
      carouselApi.off('select', updateScrollState)
      carouselApi.off('reInit', updateScrollState)
    }
  }, [carouselApi])

  return (
    <div className="bg-linear-to-b mt-9 flex flex-col gap-7 overflow-hidden rounded-t-[60px] from-neutral-blue-800 to-neutral-blue-900 to-50% desktop-xs:gap-10">
      <div className="mx-auto flex flex-col items-center justify-center gap-4 pt-7 font-inter desktop-xs:pt-[60px] desktop-lg:pt-[100px]">
        <div className="flex size-12 items-center justify-center rounded-[5px] bg-white">
          <ShieldIcon className="size-7 stroke-[#017853] stroke-2" />
        </div>
        <h3 className="px-3 text-center text-h-6 font-semibold mobile:px-8 tablet:text-h-5 desktop-xs:text-h-3">
          Become a part of our partnered communities
        </h3>
        <p className="max-w-[550px] px-3 text-center text-p-2 font-normal text-neutral-blue-500 mobile:px-8 desktop-xs:text-p-3">
          Find your new racing family. From casual Trackmania to veteran iRacing
          players, we&apos;ve got you covered.
        </p>
        <Button
          variant="outline"
          asChild
          className="hover:border-shades-0! hover:bg-shades-0! hidden transition-all duration-300 ease-in-out desktop-xs:block"
        >
          <Link
            className="hover:text-black! transition-all ease-in-out"
            href={viewAllCommunitiesHref}
            prefetch={false}
          >
            View all
          </Link>
        </Button>
      </div>

      <div className="desktop-xs:screen-grid overflow-hidden">
        {!featuredCommunities?.length ? (
          <Error
            icon="exclamation"
            size="hero"
            className="mx-auto my-16 w-full max-w-7xl bg-red-600 text-white"
            error={{
              code: 404,
              message: 'Unable to load communities.',
            }}
          />
        ) : (
          <div className="desktop-xs:grid-indent relative min-w-0">
            {canScrollPrev && (
              <button
                type="button"
                onClick={() => carouselApi?.scrollPrev()}
                aria-label="Previous communities"
                className="carousel-btn-bg group absolute -left-20 top-1/2 z-10 hidden -translate-y-1/2 cursor-pointer flex-col justify-center rounded-sm bg-neutral-blue-900 transition-all desktop-xs:flex desktop-lg:w-[109px]"
              >
                <div className="mx-auto flex size-12 items-center justify-center rounded-full border border-neutral-blue-600 bg-neutral-blue-700 group-hover:bg-neutral-blue-600">
                  <ArrowLeft className="m-auto h-4 w-4 stroke-white stroke-2" />
                </div>
              </button>
            )}

            <Carousel
              setApi={setCarouselApi}
              opts={{
                align: 'center',
                slidesToScroll: 'auto',
                containScroll: false,
                breakpoints: {
                  '(min-width: 1024px)': {
                    align: 'start',
                    containScroll: 'trimSnaps',
                  },
                },
              }}
              className="w-full min-w-0"
            >
              <CarouselContent className="pr-6 desktop-xs:pr-0">
                {featuredCommunities.map((community) => (
                  <CarouselItem
                    key={community.id || community.slug}
                    className="basis-[280px] pl-4 last:pr-4 sm:basis-[300px] desktop-xs:basis-1/4 desktop-xs:pl-4 desktop-xs:last:pr-0"
                  >
                    <CommunityCard community={community} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            {canScrollNext && (
              <button
                type="button"
                onClick={() => carouselApi?.scrollNext()}
                aria-label="Next communities"
                className="carousel-btn-bg group absolute -right-20 top-1/2 z-10 hidden -translate-y-1/2 cursor-pointer flex-col justify-center rounded-sm bg-neutral-blue-900 transition-all desktop-xs:flex desktop-lg:w-[109px]"
              >
                <div className="mx-auto flex size-12 items-center justify-center rounded-full border border-neutral-blue-600 bg-neutral-blue-700 group-hover:bg-neutral-blue-600">
                  <ArrowRight className="m-auto h-4 w-4 stroke-white stroke-2" />
                </div>
              </button>
            )}
          </div>
        )}
      </div>

      <div className="screen-grid *:grid-indent grid desktop-xs:hidden">
        <Button
          variant="outline"
          asChild
          className="scroll-py-3! px-4! hover:border-shades-0! hover:bg-white! block w-full text-center transition-all duration-300 ease-in-out desktop-xs:hidden"
        >
          <Link
            className="hover:text-black! transition-all ease-in-out"
            href={viewAllCommunitiesHref}
            prefetch={false}
          >
            View all
          </Link>
        </Button>
      </div>
    </div>
  )
}

export function MobileCarousel({ featuredCommunities = [] }) {
  if (!featuredCommunities?.length) {
    return (
      <Error
        icon="exclamation"
        size="hero"
        className="mx-auto my-16 w-full max-w-7xl bg-red-600 text-white"
        error={{ code: 404, message: 'Unable to load communities.' }}
      />
    )
  }

  return (
    <Carousel
      opts={{ align: 'center', slidesToScroll: 'auto', containScroll: false }}
      className="w-full"
    >
      <CarouselContent>
        {featuredCommunities.map((community) => (
          <CarouselItem
            key={community.id || community.slug}
            className="basis-[280px] pl-4 last:pr-4 sm:basis-[300px]"
          >
            <CommunityCard community={community} />
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  )
}

export function DesktopCarousel({ featuredCommunities = [] }) {
  if (!featuredCommunities?.length) {
    return (
      <Error
        icon="exclamation"
        size="hero"
        className="mx-auto my-16 w-full max-w-7xl bg-red-600 text-white"
        error={{ code: 404, message: 'Unable to load communities.' }}
      />
    )
  }

  return (
    <Carousel opts={{ align: 'start', slidesToScroll: 2 }} className="w-full">
      <CarouselContent>
        {featuredCommunities.map((community) => (
          <CarouselItem
            key={community.id || community.slug}
            className="basis-1/4 first:pl-8"
          >
            <CommunityCard community={community} />
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  )
}

export function CommunityCard({ community }) {
  return (
    <div className="flex h-full w-full min-w-0 flex-col justify-center rounded-sm border border-neutral-blue-700 bg-neutral-blue-800">
      <Image
        alt={`${community.name} logo`}
        height={900}
        width={1600}
        sizes="(min-width: 1280px) 300px, (min-width: 1024px) calc((100vw - 96px) / 4), (min-width: 640px) 300px, 280px"
        className="aspect-video rounded-[3px] object-cover"
        src={community.headerImgUrl || '/img/default/header.webp'}
      />

      <div className="relative flex h-0 justify-center">
        <div className="absolute mx-auto mt-[-39px] rounded-sm border-2 border-white bg-neutral-900">
          <Image
            alt={`${community.name} logo`}
            height={800}
            width={800}
            sizes="76px"
            className="h-[76px] w-[76px] min-w-[76px] rounded-[3px] object-cover"
            src={community.logoImgUrl || '/img/default/header.webp'}
          />
        </div>
      </div>

      <div className="flex h-[228px] flex-col">
        <Link
          href={`/c/${community.slug}`}
          className="group mt-[39px] flex h-[62px] w-full shrink-0 items-center justify-center px-5 py-2"
          prefetch={false}
        >
          <h6 className="truncate-2 wrap-break-word w-full text-center text-h-6 font-semibold">
            {community.name}
          </h6>
        </Link>
        <div className="text-center text-p-2 font-semibold text-neutral-blue-200">
          {getCommunityMembersCount(community)} Members
        </div>
        <CommunityEstablishedBadge
          className="mx-auto mb-2 mt-2"
          createTs={community.createTs}
        />
        <Button
          variant="outline"
          asChild
          className="border-neutral-blue-600! bg-neutral-blue-700! hover:border-shades-0! hover:bg-shades-0! mx-3 mb-3 mt-auto w-[calc(100%-24px)] transition-all duration-300 ease-in-out"
        >
          <Link
            href={`/c/${community.slug}`}
            className="hover:text-black! transition-all"
            prefetch={false}
          >
            View Community
          </Link>
        </Button>
      </div>
    </div>
  )
}
